import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useAllEmployees } from '~/data/employees';
import { VacantionBalancesReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common/useReportNavigateToView';

import {
  VacationBalancesReportRow,
  VacationBalancesReportRow_Employee,
  VacationBalancesReportRow_ExtraVacationBalance,
  VacationBalancesReportRow_Position,
  VacationBalancesReportRow_SuplementalBalance1,
  VacationBalancesReportRow_SuplementalBalance2,
  VacationBalancesReportRow_VacationBalance,
  VacationBalancesReportRow_VacationBank1,
  VacationBalancesReportRow_VacationBank2,
  VacationBalancesReportRow_VacationBank3,
  convertVacationBalancesReportDataToRows,
} from './data';

type Props = {
  data: VacantionBalancesReportData[];
};

export const useVacationBalancesReport = ({ data }: Props) => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();

  const { applicationConfig } = useApplicationConfig();
  const vacationBanksEnabled =
    applicationConfig.fComConfig.absConfig.fComEnableVacationBank;

  const { rows } = useMemo(() => {
    return {
      rows: convertVacationBalancesReportDataToRows({ data, getEmployeeById }),
    };
  }, [data, getEmployeeById]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<VacationBalancesReportRow_Employee>[] =
      [
        {
          field: 'employeeId',
          headerName: t('laPicture'),
          filterable: false,
          sortable: false,
          disableExport: true,
          renderCell: (params) => {
            return (
              <EmployeeCellComponent
                {...params}
                pictureOnly={true}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
              />
            );
          },
          minWidth: 60,
        },
        {
          field: 'firstName',
          headerName: t('laFirstName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
              />
            );
          },
          minWidth: 90,
        },
        {
          field: 'lastName',
          headerName: t('laLastName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
              />
            );
          },
          minWidth: 110,
        },
        {
          field: 'employeeNumber',
          headerName: t('laEmpId'),
          minWidth: 130,
        },
      ];

    const positionColumnConfigs: GridColDef<VacationBalancesReportRow_Position>[] =
      [
        {
          field: 'organizationUnit',
          headerName: t('laOrgUnit'),
          minWidth: 150,
        },
        {
          field: 'organizationUnitNumber',
          headerName: t('laUnitNumber'),
          minWidth: 110,
        },
        { field: 'costCenter', headerName: t('laCostCenter'), minWidth: 210 },
        {
          field: 'costCenterCode',
          headerName: t('laCostCenterCode'),
          minWidth: 150,
        },
        { field: 'legalEntity', headerName: t('laLegalEntity'), minWidth: 200 },
        {
          field: 'legalEntityCode',
          headerName: t('laLegalEntityCode'),
          minWidth: 150,
        },
        {
          field: 'managerNumber',
          headerName: t('laManagerNumber'),
          minWidth: 150,
        },
        {
          field: 'manager',
          headerName: t('laManager'),
          minWidth: 150,
        },
      ];

    const vacationBalanceColumnConfigs: GridColDef<VacationBalancesReportRow_VacationBalance>[] =
      [
        {
          field: 'balance',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'approved',
          headerName: t('laApproved'),
          minWidth: 60,
        },
        {
          field: 'inProgress',
          headerName: t('laInProgress'),
          minWidth: 60,
        },
        {
          field: 'remainingBalance',
          headerName: t('laRemaining'),
          minWidth: 130,
        },
      ];

    const extraVacationBalanceColumnConfigs: GridColDef<VacationBalancesReportRow_ExtraVacationBalance>[] =
      [
        {
          field: 'extraBalance',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'extraBalanceApproved',
          headerName: t('laApproved'),
          minWidth: 60,
        },
        {
          field: 'extraBalanceInProgress',
          headerName: t('laInProgress'),
          minWidth: 60,
        },
      ];

    const supplementalBalance1ColumnConfigs: GridColDef<VacationBalancesReportRow_SuplementalBalance1>[] =
      [
        {
          field: 'supplementalBalance1',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'supplementalSpent1',
          headerName: t('laSpent'),
          minWidth: 60,
        },
      ];

    const supplementalBalance2ColumnConfigs: GridColDef<VacationBalancesReportRow_SuplementalBalance2>[] =
      [
        {
          field: 'supplementalBalance2',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'supplementalSpent2',
          headerName: t('laSpent'),
          minWidth: 60,
        },
      ];

    const bank1VacationBalanceColumnConfigs: GridColDef<VacationBalancesReportRow_VacationBank1>[] =
      [
        {
          field: 'bank1Balance',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'bank1BalanceApproved',
          headerName: t('laApproved'),
          minWidth: 60,
        },
        {
          field: 'bank1BalanceInProgress',
          headerName: t('laInProgress'),
          minWidth: 60,
        },
      ];

    const bank2VacationBalanceColumnConfigs: GridColDef<VacationBalancesReportRow_VacationBank2>[] =
      [
        {
          field: 'bank2Balance',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'bank2BalanceApproved',
          headerName: t('laApproved'),
          minWidth: 60,
        },
        {
          field: 'bank2BalanceInProgress',
          headerName: t('laInProgress'),
          minWidth: 60,
        },
      ];

    const bank3VacationBalanceColumnConfigs: GridColDef<VacationBalancesReportRow_VacationBank3>[] =
      [
        {
          field: 'bank3Balance',
          headerName: t('laBalance'),
          minWidth: 60,
        },
        {
          field: 'bank3BalanceApproved',
          headerName: t('laApproved'),
          minWidth: 60,
        },
        {
          field: 'bank3BalanceInProgress',
          headerName: t('laInProgress'),
          minWidth: 60,
        },
      ];

    const columnConfigs = [
      ...employeeColumnConfigs,
      ...positionColumnConfigs,
      ...vacationBalanceColumnConfigs,
      ...(vacationBanksEnabled
        ? [
            ...bank1VacationBalanceColumnConfigs,
            ...bank2VacationBalanceColumnConfigs,
            ...bank3VacationBalanceColumnConfigs,
          ]
        : [
            ...extraVacationBalanceColumnConfigs,
            ...supplementalBalance1ColumnConfigs,
            ...supplementalBalance2ColumnConfigs,
          ]),
    ] as GridColDef<VacationBalancesReportRow>[];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'position',
        headerName: t('laPosition'),
        children: getFieldNamesFromColumnConfig(positionColumnConfigs),
      },
      {
        groupId: 'vacationBalance',
        headerName: t('laVacBal'),
        children: getFieldNamesFromColumnConfig(vacationBalanceColumnConfigs),
      },
      {
        groupId: 'extraVacationBalance',
        headerName: t('laVacBalExtra'),
        children: getFieldNamesFromColumnConfig(
          extraVacationBalanceColumnConfigs,
        ),
      },
      {
        groupId: 'supplementalBalance1',
        headerName: t('laSupplementalBalance1'),
        children: getFieldNamesFromColumnConfig(
          supplementalBalance1ColumnConfigs,
        ),
      },
      {
        groupId: 'supplementalBalance2',
        headerName: t('laSupplementalBalance2'),
        children: getFieldNamesFromColumnConfig(
          supplementalBalance2ColumnConfigs,
        ),
      },
      {
        groupId: 'bank1VacationBalance',
        headerName: t('laVacationBank1'),
        children: getFieldNamesFromColumnConfig(
          bank1VacationBalanceColumnConfigs,
        ),
      },
      {
        groupId: 'bank2VacationBalance',
        headerName: t('laVacationBank2'),
        children: getFieldNamesFromColumnConfig(
          bank2VacationBalanceColumnConfigs,
        ),
      },
      {
        groupId: 'bank3VacationBalance',
        headerName: t('laVacationBank3'),
        children: getFieldNamesFromColumnConfig(
          bank3VacationBalanceColumnConfigs,
        ),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, vacationBanksEnabled]);

  return {
    table: {
      columnConfigs,
      columnGroupingModel,
      rows,
    },
  };
};
